import { getLocalStorage } from "../utils";
import { getCookie } from "./cookie";
import { STORAGE_KEYS, ROUTES } from "../constants";
import { POST_LOGIN_INTENT_KEY } from "../constants/postLoginIntent";
import { isNativeApp } from "../components/mobile/native";

async function apiRequest(url, options = {}) {
  const token = getLocalStorage(STORAGE_KEYS.TOKEN);
  const csrfToken = getCookie("csrftoken");
  // Shared-ET auth is scoped to the /s/ pages so a leftover token can never affect the
  // normal app. On a share page everyone (even a logged-in visitor) acts as a share guest
  // via X-Share-Token; everywhere else we use the normal Bearer login.
  let shareToken = null;
  let staffShare = false;
  let staffPass = null;
  try {
    shareToken = sessionStorage.getItem("cc_share_token");
    staffShare = sessionStorage.getItem("cc_share_staff") === "1";
    // The admin's "Open ET" pass is bound to one share; never send it with another.
    if (sessionStorage.getItem("cc_staff_pass_for") === shareToken) {
      staffPass = sessionStorage.getItem("cc_staff_pass");
    }
  } catch (e) { /* ignore */ }
  const onSharePage = typeof window !== "undefined" && window.location.pathname.startsWith("/s/");
  const useShare = onSharePage && !!shareToken;

  if (!csrfToken) {
    console.warn("[apiRequest] CSRF token not found in cookies for:", url);
  }

  const defaultHeaders = {
    "X-CSRFToken": csrfToken,
  };

  if (useShare) {
    defaultHeaders["X-Share-Token"] = shareToken;
    // A staff-only link (the admin's "Open ET") also needs a staff credential: the signed
    // pass that link carried, else the visitor's own login. It rides in its own header so
    // the Bearer-first views still treat this as a share guest.
    const staffAuth = staffPass || token;
    if (staffShare && staffAuth) defaultHeaders["X-Staff-Auth"] = staffAuth;
  } else if (token) {
    defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  const mergedOptions = {
    ...options,
    credentials: "include",
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  const response = await fetch(url, mergedOptions);

  // Don't bounce a shared-ET guest to login on 401 — they have no account; just let the
  // caller handle it (e.g. show "this link expired").
  // Also NEVER clear the token / redirect when we're inside an iframe (the embedded editor on
  // the home page): localStorage is shared same-origin, so wiping it here would log the real
  // user out of the parent tab.
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  // Public marketing pages must never bounce a visitor to login. They're the whole top of
  // the funnel: someone arrives from a link, a widget on the page quietly makes an
  // authenticated call, gets a 401 because they've never signed up — and the app throws them
  // at a login wall before they've read a word. They should only meet login when THEY choose
  // to do something (upload, subscribe), not because a background request failed.
  const PUBLIC_PATHS = ["/flagship", "/simple", "/plugin", "/w", "/x", "/y", "/z", "/get", "/plugin-download", "/pricing", "/contact", "/app", "/privacy", "/terms", "/delete-account", "/ai"];
  const onPublicPage =
    typeof window !== "undefined" &&
    PUBLIC_PATHS.some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(p + "/"),
    );
  // The phone app owns its own sign-in and has no /login route to be sent to. Letting this
  // clear the token there logged people out of the app for a single unlucky 401 — and then
  // hard-navigated the WebView to a page that does not exist in the shell.
  if (response.status === 401 && !useShare && !inIframe && !onPublicPage && !isNativeApp()) {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    if (typeof window !== "undefined") {
      // There is no ROUTES.LOGIN: this used to set the address to "undefined", so everyone whose
      // sign-in ran out (in the library, in the editor) landed on the "Oops" 404 page. Send them
      // to sign in, and back to where they were once they have.
      try {
        sessionStorage.setItem(POST_LOGIN_INTENT_KEY, window.location.pathname + window.location.search);
      } catch (e) { /* ignore */ }
      window.location.href = ROUTES.LANDING;
    }
  }

  return response;
}

export default apiRequest;
